if (!customElements.get('sticky-cta-bar')) {
  class StickyCtaBar extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.targetSelector = this.dataset.target || '.main-product__buy-buttons';
      this.observer = null;
      this.isVisible = false;
      this.mainSectionId = null;

      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('.sticky-cta__submit');
      this.variantSelects = this.querySelectorAll('.sticky-cta__variant-select');
      this.quantityInput = this.querySelector('.sticky-cta__quantity-input');
      this.quantityButtons = this.querySelectorAll('.sticky-cta__quantity-button');
      this.priceCurrentEl = this.querySelector('[data-price-current]');
      this.priceCompareEl = this.querySelector('[data-price-compare]');
      this.variantInput = this.querySelector('input[name="id"]');

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleQuantityChange = this.handleQuantityChange.bind(this);
      this.handleQuantityButton = this.handleQuantityButton.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.onExternalVariantChange = this.onExternalVariantChange.bind(this);
      this.onExternalQuantityChange = this.onExternalQuantityChange.bind(this);
    }

    connectedCallback() {
      this.resolveMainSectionId();
      this.initObserver();
      this.attachEventListeners();
      this.subscribeToPubSub();

      this._abortController = new AbortController();
      this._publishHeight();
      window.addEventListener('resize', theme.utils.debounce(() => this._publishHeight(), 100), {
        signal: this._abortController.signal,
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.detachEventListeners();
      this.unsubscribeFromPubSub();
      this._abortController?.abort();
      document.documentElement.style.setProperty('--sticky-cta-height', '0px');
    }

    _publishHeight() {
      const isVisible = this.isVisible && getComputedStyle(this).display !== 'none';
      const isTopVariant = this.classList.contains('sticky-cta--top');
      const height = isVisible && !isTopVariant ? this.offsetHeight : 0;
      document.documentElement.style.setProperty('--sticky-cta-height', `${height}px`);
    }

    resolveMainSectionId() {
      const target = document.querySelector(this.targetSelector);
      if (!target) return;

      const mainSection = target.closest('section[id]');
      if (mainSection) {
        this.mainSectionId = mainSection.id.replace('shopify-section-', '');
        return;
      }

      const productInfo = document.querySelector('product-info[data-section-id]');
      if (productInfo) {
        this.mainSectionId = productInfo.dataset.sectionId;
        return;
      }

      console.warn('[sticky-cta-bar] Could not resolve main product sectionId — sticky→main sync disabled');
    }

    initObserver() {
      const target = document.querySelector(this.targetSelector);
      if (!target) {
        console.warn(`[sticky-cta-bar] Target "${this.targetSelector}" not found`);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.isIntersecting ? this.hide() : this.show();
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(target);
    }

    attachEventListeners() {
      this.variantSelects.forEach((select) => {
        select.addEventListener('change', this.handleVariantChange);
      });

      this.quantityInput?.addEventListener('change', this.handleQuantityChange);

      this.quantityButtons.forEach((button) => {
        button.addEventListener('click', this.handleQuantityButton);
      });

      this.form?.addEventListener('submit', this.handleSubmit);
    }

    detachEventListeners() {
      this.variantSelects.forEach((select) => {
        select.removeEventListener('change', this.handleVariantChange);
      });

      this.quantityInput?.removeEventListener('change', this.handleQuantityChange);

      this.quantityButtons.forEach((button) => {
        button.removeEventListener('click', this.handleQuantityButton);
      });

      this.form?.removeEventListener('submit', this.handleSubmit);
    }

    subscribeToPubSub() {
      if (!theme.pubsub) return;

      this.unsubscribeVariant = theme.pubsub.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this.onExternalVariantChange
      );

      this.unsubscribeQuantity = theme.pubsub.subscribe(
        theme.pubsub.events.QUANTITY_UPDATE,
        this.onExternalQuantityChange
      );
    }

    unsubscribeFromPubSub() {
      this.unsubscribeVariant?.();
      this.unsubscribeQuantity?.();
    }

    show() {
      if (this.isVisible) return;
      this.isVisible = true;
      this.classList.add('is-visible');
      this._publishHeight();
    }

    hide() {
      if (!this.isVisible) return;
      this.isVisible = false;
      this.classList.remove('is-visible');
      this._publishHeight();
    }

    handleVariantChange() {
      const variant = this.getCurrentVariant();
      if (!variant) return;

      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateSubmitButton(variant);
      this.publishVariantChange(variant);
    }

    handleQuantityChange() {
      this.validateQuantity();
      this.publishQuantityChange();
    }

    handleQuantityButton(event) {
      const button = event.currentTarget;
      const isPlus = button.name === 'plus';
      const currentValue = parseInt(this.quantityInput.value, 10) || 1;
      const min = parseInt(this.quantityInput.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || Infinity;

      const newValue = Math.max(min, Math.min(max, isPlus ? currentValue + 1 : currentValue - 1));

      if (newValue !== currentValue) {
        this.quantityInput.value = newValue;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    validateQuantity() {
      if (!this.quantityInput) return;

      let value = parseInt(this.quantityInput.value, 10);
      const min = parseInt(this.quantityInput.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || Infinity;

      if (isNaN(value) || value < min) {
        value = min;
      } else if (value > max) {
        value = max;
      }

      this.quantityInput.value = value;
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.submitButton?.disabled) return;

      const variantId = this.variantInput?.value;
      if (!variantId) return;

      const quantity = parseInt(this.quantityInput?.value || '1', 10);

      theme.cart.errors.clearProductForm(variantId);

      this.setLoading(true);
      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.setLoading(true);

      try {
        const data = await theme.cart.add({ id: variantId, quantity }, { openDrawer: true });

        if (cartDrawer?.renderContents) {
          cartDrawer.renderContents(data);
        }
        cartDrawer?.setLoading(false);

        theme.pubsub?.publish(theme.pubsub.events.CART_UPDATE, {
          source: 'sticky-cta',
          productVariantId: variantId,
          cartData: data,
          skipFetch: true,
        });
      } catch (error) {
        cartDrawer?.setLoading(false);
        cartDrawer?.close();
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.setAttribute('aria-busy', isLoading.toString());
    }

    getCurrentVariant() {
      const variantData = this.querySelector('[data-variants]');
      if (!variantData) return null;

      const variants = JSON.parse(variantData.textContent);
      const selectedOptions = this.getSelectedOptions();

      return variants.find((variant) =>
        variant.options.every((option, index) => option === selectedOptions[index])
      );
    }

    getSelectedOptions() {
      const options = [];
      this.variantSelects.forEach((select) => {
        options.push(select.value);
      });
      return options;
    }

    updateVariantInput(variant) {
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }
    }

    updatePrice(variant) {
      if (this.priceCurrentEl) {
        this.priceCurrentEl.textContent = theme.utils.formatMoney(variant.price);
      }

      if (this.priceCompareEl) {
        if (variant.compare_at_price > variant.price) {
          this.priceCompareEl.textContent = theme.utils.formatMoney(variant.compare_at_price);
          this.priceCompareEl.classList.remove('is-hidden');
        } else {
          this.priceCompareEl.classList.add('is-hidden');
        }
      }
    }

    updateSubmitButton(variant) {
      if (!this.submitButton) return;

      const buttonText = this.submitButton.querySelector('.sticky-cta__submit-text');
      if (!buttonText) return;

      if (!variant) {
        this.submitButton.disabled = true;
        buttonText.textContent = theme.locales?.product?.unavailable;
        return;
      }

      if (variant.available) {
        this.submitButton.disabled = false;
        buttonText.textContent = theme.locales?.product?.addToCart;
      } else {
        this.submitButton.disabled = true;
        buttonText.textContent = theme.locales?.product?.soldOut;
      }
    }

    publishVariantChange(variant) {
      theme.pubsub?.publish(theme.pubsub.events.VARIANT_CHANGE, {
        source: 'sticky-cta',
        sectionId: this.mainSectionId ?? this.sectionId,
        variant: variant,
        selectedOptions: this.getSelectedOptions(),
      });
    }

    publishQuantityChange() {
      theme.pubsub?.publish(theme.pubsub.events.QUANTITY_UPDATE, {
        source: 'sticky-cta',
        sectionId: this.mainSectionId ?? this.sectionId,
        quantity: parseInt(this.quantityInput?.value, 10) || 1,
      });
    }

    onExternalVariantChange(data) {
      if (data.source === 'sticky-cta') return;

      if (data.selectedOptions) {
        data.selectedOptions.forEach((value, index) => {
          if (this.variantSelects[index]) {
            this.variantSelects[index].value = value;
          }
        });
      }

      if (data.variant) {
        this.updateVariantInput(data.variant);
        this.updatePrice(data.variant);
        this.updateSubmitButton(data.variant);
      }
    }

    onExternalQuantityChange(data) {
      if (data.source === 'sticky-cta') return;

      if (this.quantityInput && data.quantity) {
        this.quantityInput.value = data.quantity;
      }
    }
  }

  customElements.define('sticky-cta-bar', StickyCtaBar);
}
